import { site } from "config";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import SocialShare from "components/links/SocialLinks/SocialShare";
import GuideData from "./components/GuideData";
import GuideNav from "./components/GuideNav";
import Header from "./components/Header";
import KeyFacts from "./section/KeyFacts";
import PhrasesSection from "./section/PhrasesSection";
const GuideMap = dynamic(() => import("components/guide/components/GuideMap"));
const OpenCloseButton = dynamic(
  () => import("components/ui/buttons/OpenCloseButton/OpenCloseButton")
);
const Itinerary = dynamic(() => import("./itinerary/Itinerary"));
const InfoSection = dynamic(() => import("./section/InfoSection"));
const FoodGuideCards = dynamic(
  () => import("components/FoodGuides/FoodGuideCards")
);
const OpenWeatherWidget = dynamic(
  () => import("../widgets/weather/OpenWeatherWidget/OpenWeatherWidget")
);
const RelatedExperiences = dynamic(
  () =>
    import("../ExperiencePresentation/RelatedExperiences/RelatedExperiences")
);

import { AiOutlineRead } from "react-icons/ai";
import { BiBuildingHouse, BiCloud, BiTrip } from "react-icons/bi";
import { BsSignpost2Fill } from "react-icons/bs";
import { FaWalking } from "react-icons/fa";
import { MdOutlineRestaurant } from "react-icons/md";

import { formatLocationString } from "./utils/format";

import styles from "./Guide.module.scss";

export default function Guide({ data, relatedExperiences, foodGuide }) {
  const [itineraryItems, setItineraryItems] = useState(data?.itinerary ?? []);
  const [viatorExperiences, setViatorExperiences] = useState([]);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [screenSize, setScreenSize] = useState(0);

  const router = useRouter();

  useEffect(() => {
    setScreenSize(window.innerWidth);

    window.addEventListener("resize", () => setScreenSize(window.innerWidth));
    return () => {
      window.removeEventListener("resize", () =>
        setScreenSize(window.innerWidth)
      );
    };
  }, []);

  const {
    author,
    itinerary,
    location,
    metadata,
    introduction,
    travel_logistics,
    climate_and_seasons,
    accommodations,
    food_and_drink,
    attractions_and_activities,
    other_experiences,
    preferences,
    trip_data,
    seo,
    images,
    key_facts,
    useful_phrases,
    slug,
  } = data;

  const { city, destinations } = location;
  const { createdAt } = metadata;
  const locationString = formatLocationString({ location: { city } });
  const title = `Travel Guide for ${locationString}`;
  const itineraryLength = itinerary?.length;

  useEffect(() => {
    if (!itinerary?.length) {
      return;
    }
    if (itinerary[0].id !== itineraryItems[0].id) {
      setItineraryItems(itinerary);
    }
  }, [itinerary, itineraryItems]);

  if (!location) {
    return null;
  }

  const weatherWidget = (
    <OpenWeatherWidget
      location={{ latitude: location?.lat, longitude: location?.lon }}
      style="fill"
    />
  );

  const socialShareConfig = {
    text: `I just planned my trip to ${locationString} with WhereNext! `,
    title: `Travel Guide for ${locationString}`,
    url: `${site}${router.asPath}`.split("?#")[0].toString(),
  };

  return (
    <div key={router.asPath}>
      <Header
        title={title}
        author={author}
        date={createdAt}
        location={location}
        destination={destinations[0]}
        image={images?.[0]?.url ?? seo?.metaMedia?.url}
        imageAlt={
          images?.[0]?.alternativeText ??
          seo?.metaMedia?.alternativeText ??
          `Picture of ${locationString}`
        }
        socialShareConfig={socialShareConfig}
        format="image"
        screenSize={screenSize}
        id={data?.id}
      />
      <GuideNav data={data} />
      <main className={`${styles.guideContent}`}>
        <div className={`${styles.fullWidth}`}>
          <GuideData
            preferences={preferences}
            trip_data={trip_data}
            image={seo?.metaMedia}
            location={location}
            itineraryLength={itineraryLength}
          />
          {key_facts && (
            <section className={`${styles.section}`} id="key-facts">
              <KeyFacts facts={key_facts} />
            </section>
          )}

          {useful_phrases &&
            useful_phrases?.language?.toLowerCase() !== "english" &&
            useful_phrases?.thank_you?.native?.toLowerCase() !==
              "thank you" && (
              <section className={`${styles.section}`} id="useful-phrases">
                <PhrasesSection phrases={useful_phrases} />
              </section>
            )}
        </div>
        <div className={`${styles.layout}`}>
          <div className={`${styles.mapToggleButton}`}>
            <OpenCloseButton
              active={isMapOpen}
              onClick={() => setIsMapOpen((prev) => !prev)}
              size={38}
            />
          </div>
          <div className={`${styles.sections}`}>
            {introduction && (
              <InfoSection
                title="Introduction"
                id="introduction"
                text={introduction?.content}
                icon={<AiOutlineRead size={34} />}
              />
            )}
            {travel_logistics && (
              <InfoSection
                title="Getting There"
                id="travel_logistics"
                text={travel_logistics?.content}
                icon={<BiTrip size={34} />}
              />
            )}
            {climate_and_seasons && (
              <InfoSection
                title="Climate and Best Time to Visit"
                id="climate_and_seasons"
                text={climate_and_seasons?.content}
                icon={<BiCloud size={34} />}
                topRow={weatherWidget}
              />
            )}
            {itineraryItems && itineraryItems[0]?.id && (
              <Itinerary
                items={itineraryItems}
                location={location}
                setItineraryItems={setItineraryItems}
                key={`${router.asPath}-${itineraryItems[0]?.id}`}
                slug={slug}
              />
            )}
            {accommodations && (
              <InfoSection
                title="Accommodations"
                id="accommodations"
                text={accommodations?.content}
                icon={<BiBuildingHouse size={34} />}
              />
            )}
            {food_and_drink && (
              <>
                <InfoSection
                  title="Food and Drink"
                  id="food_and_drink"
                  text={food_and_drink?.content}
                  icon={<MdOutlineRestaurant size={34} />}
                />
                {foodGuide?.food && <FoodGuideCards foods={foodGuide?.food} />}
              </>
            )}
            {attractions_and_activities && (
              <InfoSection
                title="Attractions and Activities"
                id="attractions_and_activities"
                text={attractions_and_activities?.content}
                data={{ items: attractions_and_activities?.items, location }}
                setViatorExperiences={setViatorExperiences}
                showViator={true}
                icon={<FaWalking size={34} />}
              />
            )}
            {other_experiences && (
              <InfoSection
                title="Off-the-Beaten-Path Experiences"
                id="other_experiences"
                text={other_experiences?.content}
                data={{ items: other_experiences?.items, location }}
                setViatorExperiences={setViatorExperiences}
                showViator={true}
                icon={<BsSignpost2Fill size={34} />}
              />
            )}
          </div>
          <div
            className={`${styles.map} ${
              isMapOpen ? styles.open : styles.closed
            }`}
          >
            {location?.lat && location?.lon && (
              <GuideMap
                initialPosition={{ lat: location?.lat, lon: location?.lon }}
                location={{
                  ...location,
                  viator_destination_info:
                    destinations[0]?.destination_details
                      ?.viator_destination_info ?? {},
                }}
                itineraryItems={itineraryItems?.length ? itineraryItems : []}
                viatorExperiences={
                  viatorExperiences?.length ? viatorExperiences : []
                }
                key={data?.id}
              />
            )}
          </div>
        </div>
        {!!relatedExperiences?.length && (
          <div className={`${styles.fullWidth} ${styles.section}`}>
            <div className={`${styles.relatedExperiences}`}>
              <RelatedExperiences
                experiences={relatedExperiences}
                sideScroll="all"
                title="Related Experiences"
                sideScrollButtons={true}
              />
            </div>
          </div>
        )}
      </main>

      <footer className={`${styles.guideFooter}`}>
        <section className={`${styles.shareContainer}`}>
          <h2 className={`${styles.heading}`}>Share and Create</h2>
          <p>
            Share your travel insights or create a new guide for fellow
            adventurers!
          </p>
          <SocialShare
            url={`${site}${router.asPath}`}
            direction="horizontal"
            styled={true}
            style="dropdown"
            config={socialShareConfig}
            ctaTitle="Create Your Own Guide"
            ctaLink="/travel-guides/create"
            // copyUrl={true}
          />
        </section>
      </footer>
    </div>
  );
}
